import { getCO2SpotChart } from "$/api/market-monitoring/other-markets.api";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import Flexbox from "$/lib/components/common/Flexbox";
import GraphFilter from "$/lib/components/graphs/GraphFilter/GraphFilter";
import type { GraphFilterItemsProps } from "$/lib/components/graphs/GraphFilter/GraphFilterItem";
import LineGraph from "$/lib/components/graphs/LineGraph";
import ChartsContainer from "$/lib/components/molecules/ChartsContainer";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import FiltersDisplayBox from "$/lib/components/molecules/FiltersDisplayBox";
import GraphDateRangeSelector from "$/lib/components/molecules/GraphDateRangeSelector";
import LineChartIcon from "$/lib/icons/LineChartIcon";
import type { CustomDashboardProps } from "$/lib/types/MarketMonitoring.types";
import {
  getDate,
  getLast6MonthsStartAndEndDates,
} from "$/lib/utils/functions/date.functions";
import { handleOnChangeDefaultGraphsTabs } from "$/lib/utils/functions/marketMonitoring/graphs.functions";
import { cn, getArrayKeys } from "$/lib/utils/functions/misc.functions";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

const CO2SpotChart = ({
  id,
  isDraggable = false,
  isShowcase = false,
}: CustomDashboardProps) => {
  const [selectedGraphTabId, setSelectedGraphTabId] = useState("LAST_6_MONTHS");

  const { start, end } = getLast6MonthsStartAndEndDates();

  const [dateRange, setDateRange] = useState<[Date, Date]>([start, end]);

  const { data, isLoading } = useQuery({
    queryKey: ["get-other-markets-spot-co2-chart", dateRange],
    queryFn: () =>
      getCO2SpotChart({
        date: getDate(dateRange[0]),
        endDate: getDate(dateRange[1]),
      }),
  });

  const keys = useMemo(() => getArrayKeys(data ?? []), [data]);

  const colors: Record<string, string> = keys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: "#605e5c",
    }),
    {},
  );

  const [graphVisibility, setGraphVisibility] = useState<
    GraphFilterItemsProps[]
  >([]);

  const handleOnChangeGraphVisiblity = (key: string, value: boolean) => {
    setGraphVisibility((prev) =>
      prev.map((item) =>
        item.name === key ? { ...item, isVisible: value } : item,
      ),
    );
  };

  const handleOnChangeDateRange = (values: [Date, Date]) => {
    setDateRange(values);
  };

  useEffect(() => {
    handleOnChangeDefaultGraphsTabs(
      selectedGraphTabId,
      handleOnChangeDateRange,
    );
  }, [selectedGraphTabId]);

  useEffect(() => {
    setGraphVisibility(
      keys.map((key) => ({
        name: key,
        color: "#605e5c",
      })),
    );
  }, [keys]);

  return (
    <DisplayBox
      title="EUA (CO2) Spot"
      Icon={LineChartIcon}
      theme="orange"
      containerClassName={cn("shadow-displayBoxShadow ", {
        "pointer-events-none": isShowcase,
        "w-full": isDraggable || isShowcase,
      })}
      id={id}
      isDraggable={isDraggable}
    >
      <ChartsContainer className="!h-[520px] py-0">
        {isLoading ? (
          <Flexbox fullWidth fullHeight align="center" justify="center">
            <Loader height={40} width={40} />
          </Flexbox>
        ) : (
          <LineGraph
            data={data ?? []}
            xAxisDataKey="date"
            colors={colors}
            theme="orange"
            keys={keys}
            unit=" €/t"
            leftMargin={-20}
          />
        )}

        <FiltersDisplayBox>
          <GraphDateRangeSelector
            dateRange={dateRange}
            setDateRange={setDateRange}
            selectedGraphTabId={selectedGraphTabId}
            setSelectedGraphTabId={setSelectedGraphTabId}
            theme="orange"
          />

          <GraphFilter
            title="Légende"
            data={graphVisibility}
            handleOnChange={handleOnChangeGraphVisiblity}
          />
        </FiltersDisplayBox>
      </ChartsContainer>
    </DisplayBox>
  );
};

export default CO2SpotChart;
